import React from 'react';
import { Link } from 'react-router-dom';
import { Upload, Settings, Video, Download, ArrowRight } from 'lucide-react';
import { howItWorksSteps } from '../utils/mockData';
import Reveal from '../components/Reveal';

const HowItWorksPage = () => {
  const icons = {
    Upload: Upload,
    Settings: Settings,
    Video: Video,
    Download: Download
  };

  return (
    <div className="min-h-screen bg-black pt-24 md:pt-40 pb-20">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <Reveal className="text-center mb-16">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
            How It <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Works</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            From your photos and footage to a finished cinematic presentation - here's exactly how we weave it all together.
          </p>
        </Reveal>

        {/* Steps */}
        <div className="max-w-4xl mx-auto space-y-12">
          {howItWorksSteps.map((step, index) => {
            const Icon = icons[step.icon];
            const isLast = index === howItWorksSteps.length - 1;

            return (
              <Reveal key={step.step} className="relative">
                {/* Connector Line */}
                {!isLast && (
                  <div className="hidden lg:block absolute left-10 top-24 w-0.5 h-full bg-gradient-to-b from-cyan-500 to-transparent"></div>
                )}

                <div className="flex flex-col lg:flex-row gap-8 items-start">
                  {/* Step Number & Icon */}
                  <div className="flex-shrink-0 relative">
                    <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl flex items-center justify-center shadow-2xl shadow-amber-500/30">
                      <Icon size={36} className="text-black" />
                    </div>
                    <div className="absolute -top-3 -right-3 w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                      {step.step}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 border border-gray-700 hover:border-cyan-500 transition-all duration-300 hover:shadow-xl hover:shadow-cyan-500/20">
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                      {step.title}
                    </h2>
                    <p className="text-lg text-gray-300 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* Media Cards - How customers send vs what they receive */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto items-stretch">
          {/* Media We Accept */}
          <Reveal delay={1} className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 border border-gray-700 h-full flex flex-col">
            <h3 className="text-2xl font-bold text-white mb-2">
              Media We <span className="text-cyan-400">Accept</span>
            </h3>
            <p className="text-sm text-gray-400 mb-6">
              Upload or send us your photos and videos using any of the formats and methods below.
            </p>

            <div className="space-y-5">
              <div>
                <p className="text-amber-400 font-semibold text-sm uppercase tracking-wider mb-2">Photos</p>
                <div className="flex flex-wrap gap-2">
                  {['JPG', 'JPEG', 'PNG', 'HEIC', 'RAW'].map((f) => (
                    <span key={f} className="px-3 py-1 text-xs rounded-full border border-white/10 bg-white/[0.03] text-gray-300">
                      {f}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-amber-400 font-semibold text-sm uppercase tracking-wider mb-2">Videos</p>
                <div className="flex flex-wrap gap-2">
                  {['MP4', 'MOV', 'AVI', 'WMV'].map((f) => (
                    <span key={f} className="px-3 py-1 text-xs rounded-full border border-white/10 bg-white/[0.03] text-gray-300">
                      {f}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-amber-400 font-semibold text-sm uppercase tracking-wider mb-2">Send Your Media Via</p>
                <ul className="space-y-2">
                  {[
                    'Secure upload form',
                    'Google Drive',
                    'WeTransfer',
                    'Dropbox',
                    'Email (for smaller files)',
                  ].map((method) => (
                    <li key={method} className="flex items-start gap-2 text-gray-300 text-[15px]">
                      <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full mt-2 flex-shrink-0" />
                      <span>{method}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>

          {/* What You'll Receive */}
          <Reveal delay={2} className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 border border-gray-700 h-full flex flex-col">
            <h3 className="text-2xl font-bold text-white mb-2">
              What You'll <span className="text-amber-400">Receive</span>
            </h3>
            <p className="text-sm text-gray-400 mb-6">
              Your finished cinematic presentation, delivered to enjoy anywhere.
            </p>

            <ul className="space-y-3">
              {[
                '4K quality MP4 video',
                'Delivered via secure private Google Drive download link + physical USB (both included)',
                'Professionally edited with cinematic transitions and effects',
                'Music of your choice',
                'Ready to enjoy on TVs, phones, tablets and social media',
              ].map((item) => (
                <li key={item} className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0" />
                  <span className="text-gray-300 text-[15px] leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        {/* FAQ Section */}
        <div className="mt-20 max-w-4xl mx-auto">
          <Reveal as="h2" className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
            Frequently Asked <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Questions</span>
          </Reveal>
          
          <div className="space-y-4">
            <Reveal delay={1} className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 border border-gray-700">
              <h4 className="text-lg font-semibold text-white mb-2">How long does it take?</h4>
              <p className="text-gray-400">Estimated production times: <strong className="text-amber-300">Essential 1-3 days, Signature 2-4 days, Cinematic 3-5 days</strong>. Cinematic deliberately has the longest window because it involves the most detailed editing and highest production value. Timelines begin once all your media has been received. These are estimated production times, not guaranteed delivery dates.</p>
            </Reveal>

            <Reveal delay={2} className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 border border-gray-700">
              <h4 className="text-lg font-semibold text-white mb-2">Can I request revisions?</h4>
              <p className="text-gray-400">Absolutely! Every collection includes free revisions - from 1 revision with our Essential Collection to 3 with the Cinematic Collection. Revisions are reasonable changes to the agreed creative direction (not complete redesigns). We'll work with you until it feels just right.</p>
            </Reveal>

            <Reveal delay={3} className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 border border-gray-700">
              <h4 className="text-lg font-semibold text-white mb-2">What if I don't have music in mind?</h4>
              <p className="text-gray-400">No problem! We can suggest appropriate music based on the occasion and mood you want to create.</p>
            </Reveal>

            <Reveal delay={4} className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 border border-gray-700">
              <h4 className="text-lg font-semibold text-white mb-2">How many photos can I include?</h4>
              <p className="text-gray-400">Each collection includes a set number of photos: up to 20 with the Essential Collection, up to 40 with the Signature Collection and up to 60 with the Cinematic Collection. Need to include more? Just let us know - we can tailor your project.</p>
            </Reveal>

            <Reveal delay={4} className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 border border-gray-700">
              <h4 className="text-lg font-semibold text-white mb-2">How long will my download be available?</h4>
              <p className="text-gray-400">Your private Google Drive download link remains active for 30 days after delivery. We recommend downloading and backing up your presentation as soon as you receive it. Every order also includes a physical USB copy of your finished presentation for safekeeping.</p>
            </Reveal>
          </div>
        </div>

        {/* CTA */}
        <Reveal className="mt-20 text-center bg-gradient-to-r from-cyan-900/30 to-blue-900/30 rounded-3xl p-12 border border-cyan-500/30">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready To Create Your Visual Experience?
          </h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
            The process is simple and we're here to help every step of the way
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold rounded-full hover:from-amber-600 hover:to-amber-700 transition-all duration-300 shadow-xl hover:shadow-amber-500/50 hover:scale-105 active:scale-95"
            >
              Book My Cinematic Film
              <ArrowRight size={20} className="ml-2" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-cyan-500 text-cyan-400 font-semibold rounded-full hover:bg-cyan-500 hover:text-white transition-all duration-300"
            >
              Contact Us First
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  );
};

export default HowItWorksPage;
